import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { AdminPageLayout } from "@/layouts/AdminPageLayout";
import { getLevelTestGroupsForInfiniteScroll, createLevelTestGroup } from '@/services/admin/levelTestGroups.service';
import { useAppToast } from "@/hooks/useAppToast";
import { LevelTestGroup } from '@/services/admin/levelTestGroups.interfaces';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { BaseToolbar } from "@/components/ui/base-toolbar";
import { InfiniteScroll } from "@/components/InfiniteScroll";
import { useRefresher } from "@/components/grids/DataGridComponent";

const AdminLevelTestsManagementPage: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newGroup, setNewGroup] = useState({ name: "", description: "", level: 1, skillArea: "" });
  const [isCreating, setIsCreating] = useState(false);
  const toast = useAppToast();
  const refreshHandle = useRefresher();

  const fetchGroups = async (page: number) => {
    try {
      return await getLevelTestGroupsForInfiniteScroll(page, {
        search: searchQuery
      });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Falha ao carregar grupos de teste de nivelamento");
      return [];
    }
  };

  const handleCreateGroup = async () => {
    if (!newGroup.name.trim()) {
      toast.error("Nome é obrigatório");
      return;
    }

    setIsCreating(true);
    try {
      await toast.promise(
        createLevelTestGroup({
          name: newGroup.name,
          description: newGroup.description,
          level: newGroup.level,
          skillArea: newGroup.skillArea || undefined
        }),
        {
          loading: "Criando grupo...",
          success: "Grupo criado com sucesso",
          error: "Falha ao criar grupo"
        }
      );
      
      setCreateDialogOpen(false);
      setNewGroup({ name: "", description: "", level: 1, skillArea: "" });
      refreshHandle.refresh();
    } catch (error) {
      console.error("Error creating group:", error);
    } finally {
      setIsCreating(false);
    }
  };

  const renderGroup = (group: LevelTestGroup, key: React.Key) => (
    <Link
      key={key}
      to={`/admin/educational/level-tests-management/${group.id}`}
      className="block border rounded-lg p-4 hover:border-blue-500 transition-colors"
    >
      <div className="flex items-center gap-3 mb-2">
        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 text-blue-600 font-semibold">
          {group.level}
        </div>
        <div className="flex-1">
          <h3 className="font-medium">{group.name}</h3>
          <div className="flex items-center gap-2 mt-1">
            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
              group.status === 'Published' ? 'bg-green-100 text-green-800' :
              group.status === 'Archived' ? 'bg-gray-100 text-gray-800' :
              'bg-yellow-100 text-yellow-800'
            }`}>
              {group.status === 'Published' ? 'Publicado' : group.status === 'Archived' ? 'Arquivado' : 'Rascunho'}
            </span>
            <span className="text-xs text-gray-400">
              {group.exerciseCount} exercícios
            </span>
          </div>
        </div>
      </div>
      {group.description && (
        <p className="text-sm text-gray-500 line-clamp-2 mb-2">{group.description}</p>
      )}
      <div className="text-xs text-gray-400 border-t pt-2">
        <div>Criado em: {new Date(group.createdAt).toLocaleDateString()}</div>
        {group.updatedAt && (
          <div>Atualizado em: {new Date(group.updatedAt).toLocaleDateString()}</div>
        )}
      </div>
    </Link>
  );

  const groupKey = (group: LevelTestGroup) => group.id;

  return (
    <AdminPageLayout title="Testes de Nivelamento">
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Criar novo grupo de teste de nivelamento</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Nome</label>
              <Input
                value={newGroup.name}
                onChange={(e) => setNewGroup({ ...newGroup, name: e.target.value })}
                placeholder="Digite o nome do grupo"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Descrição</label>
              <Textarea
                value={newGroup.description}
                onChange={(e) => setNewGroup({ ...newGroup, description: e.target.value })}
                placeholder="Digite a descrição do grupo"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Nível</label>
              <Input
                type="number"
                value={newGroup.level}
                onChange={(e) => setNewGroup({ ...newGroup, level: parseInt(e.target.value, 10) || 1 })}
                placeholder="Digite o nível do grupo"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Área de habilidade (opcional)</label>
              <Input
                value={newGroup.skillArea}
                onChange={(e) => setNewGroup({ ...newGroup, skillArea: e.target.value })}
                placeholder="Ex.: Gramática, Vocabulário, Compreensão"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCreateDialogOpen(false)}
              disabled={isCreating}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleCreateGroup}
              disabled={isCreating}
            >
              Criar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="space-y-6">
        <h2 className="text-lg font-medium">Grupos de teste de nivelamento</h2>
        <BaseToolbar
          searchPlaceholder="Buscar grupos de teste de nivelamento..."
          buttonLabel="Criar novo grupo"
          onCreateClick={() => setCreateDialogOpen(true)}
          onSearchChange={(value) => {
            setSearchQuery(value);
            refreshHandle.refresh(); // Refresh when search changes
          }}
          searchValue={searchQuery}
        />

        <InfiniteScroll<LevelTestGroup>
          refreshHandle={refreshHandle}
          fetchItems={fetchGroups}
          renderItem={renderGroup}
          itemKey={groupKey}
          layout="grid"
          gridCols={{ default: 2, lg: 3 }}
        />
      </div>
    </AdminPageLayout>
  );
};

export default AdminLevelTestsManagementPage;